import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '@/api/auth';
import { getUserInfo } from '@/api/agent';

function inputFocusStyle(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) {
  e.target.style.borderColor = '#0069BD';
  e.target.style.boxShadow = '0 0 0 3px rgba(0,105,189,0.08)';
}
function inputBlurStyle(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) {
  e.target.style.borderColor = '#e5e7eb';
  e.target.style.boxShadow = 'none';
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [loginForm, setLoginForm] = useState({ loginname: '', password: '' });
  const [showLoginPw, setShowLoginPw] = useState(false);

  const [isAutoLoginChecking, setIsAutoLoginChecking] = useState(true);

  // 页面加载时检测本地 token，有效则直接跳转
  useEffect(() => {
    const token = localStorage.getItem("agent_token");
    if (!token) {
      setIsAutoLoginChecking(false);
      return;
    }
    getUserInfo()
      .then((user) => {
        if (user.frontflag) {
          navigate("/library", { replace: true });
        } else {
          navigate("/admin", { replace: true });
        }
      })
      .catch(() => {
        // token 无效，清除并继续显示登录页
        localStorage.removeItem("agent_token");
        setIsAutoLoginChecking(false);
      });
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg('');
    try {
      const result = await login(loginForm.loginname, loginForm.password);
      // 存储 token 到本地
      if (result.token) {
        localStorage.setItem("agent_token", result.token);
      }
      // frontflag: 1=前端用户跳转/library, 0=后端用户跳转/admin
      if (result.user?.frontflag === 1) {
        navigate('/library');
      } else {
        navigate('/admin');
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // 自动登录检测中，显示加载状态
  if (isAutoLoginChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 40%, #e0f2fe 70%, #bfdbfe 100%)' }}>
        <i className="ri-loader-4-line animate-spin text-3xl" style={{ color: '#0069BD' }}></i>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 40%, #e0f2fe 70%, #bfdbfe 100%)' }}></div>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full opacity-40" style={{ background: 'radial-gradient(circle, #93c5fd 0%, transparent 70%)' }}></div>
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full opacity-30" style={{ background: 'radial-gradient(circle, #60a5fa 0%, transparent 70%)' }}></div>
        <svg className="absolute inset-0 w-full h-full opacity-[0.07]" xmlns="http://www.w3.org/2000/svg">
          <defs><pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse"><path d="M 60 0 L 0 0 0 60" fill="none" stroke="#1d4ed8" strokeWidth="1"/></pattern></defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
        <svg className="absolute inset-0 w-full h-full opacity-[0.12]" xmlns="http://www.w3.org/2000/svg">
          <defs><pattern id="dots" width="30" height="30" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="1.2" fill="#3b82f6"/></pattern></defs>
          <rect width="100%" height="100%" fill="url(#dots)" />
        </svg>
      </div>

      {/* Card */}
      <div className="relative z-10 w-full max-w-[440px] mx-4 my-8">
        <div className="bg-white/85 backdrop-blur-xl rounded-2xl border border-white/50 p-8" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.06)' }}>
          {/* Logo */}
          <div className="flex justify-center mb-7">
            <img src="/images/logo-full.png" alt="Sinovision" className="h-10 w-auto object-contain" />
          </div>

          {/* ──────────── LOGIN ──────────── */}
          {mode === 'login' && (
            <>
              <div className="text-center mb-7">
                <h2 className="text-xl font-bold text-gray-900">Welcome back</h2>
                <p className="text-gray-400 text-sm mt-1">Sign in with your agent credentials</p>
              </div>
              <form onSubmit={handleLogin} className="space-y-4">
                {errorMsg && (
                  <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-red-500 text-sm"></i>
                    </div>
                    <p className="text-sm text-red-700">{errorMsg}</p>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Username</label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-user-line text-sm"></i>
                    </div>
                    <input type="text" required value={loginForm.loginname} onChange={(e) => setLoginForm({ ...loginForm, loginname: e.target.value })} placeholder="Enter username" className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none transition-all bg-white/70" onFocus={inputFocusStyle} onBlur={inputBlurStyle} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Password</label>
                    <button type="button" onClick={() => setMode('forgot')} className="text-xs font-semibold cursor-pointer whitespace-nowrap" style={{ color: '#0069BD' }}>Forgot password?</button>
                  </div>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-lock-line text-sm"></i>
                    </div>
                    <input type={showLoginPw ? 'text' : 'password'} required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="Enter password" className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none transition-all bg-white/70" onFocus={inputFocusStyle} onBlur={inputBlurStyle} />
                    <button type="button" onClick={() => setShowLoginPw(!showLoginPw)} className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                      <i className={showLoginPw ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'}></i>
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={isLoading} className="w-full py-3 rounded-lg text-sm font-semibold text-white transition-all whitespace-nowrap cursor-pointer disabled:opacity-70" style={{ background: '#0069BD' }}>
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin text-sm"></i>Signing in...</span>
                  ) : 'Sign In'}
                </button>
              </form>
              <div className="mt-5 pt-5 border-t border-gray-100">
                <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg" style={{ background: 'rgba(0,105,189,0.05)', border: '1px solid rgba(0,105,189,0.12)' }}>
                  <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5" style={{ color: '#0069BD' }}>
                    <i className="ri-information-line text-sm"></i>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Account access is by invitation only. Please contact your Sinovision representative to request access.
                  </p>
                </div>
              </div>
            </>
          )}

          {/* ──────────── FORGOT PASSWORD — Contact Admin ──────────── */}
          {mode === 'forgot' && (
            <>
              <button onClick={() => { setMode('login'); setErrorMsg(''); }} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 mb-5 cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line text-sm"></i>Back to Sign In
              </button>
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(0,105,189,0.08)' }}>
                  <i className="ri-mail-send-line text-xl" style={{ color: '#0069BD' }}></i>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Forgot Password</h2>
                <p className="text-gray-400 text-sm mt-1">Self-service password reset is not available</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3 px-4 py-4 rounded-xl border" style={{ background: 'rgba(0,105,189,0.04)', borderColor: 'rgba(0,105,189,0.15)' }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(0,105,189,0.08)' }}>
                    <i className="ri-customer-service-2-line text-sm" style={{ color: '#0069BD' }}></i>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">Contact Administrator</p>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      To reset your password, please contact your system administrator. They will verify your identity and provide a new password.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-gray-200 bg-gray-50">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-white border border-gray-200">
                    <i className="ri-mail-line text-sm text-gray-500"></i>
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-xs text-gray-400 mb-0.5">Administrator Email</p>
                    <p className="text-sm font-semibold text-gray-800 truncate">admin@sinovision.com</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText('admin@sinovision.com')}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 cursor-pointer whitespace-nowrap transition-colors shrink-0"
                    title="Copy email address"
                  >
                    <i className="ri-file-copy-line mr-1"></i>Copy
                  </button>
                </div>

                <a
                  href="mailto:admin@sinovision.com?subject=Password Reset Request"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-white whitespace-nowrap cursor-pointer transition-all hover:opacity-90"
                  style={{ background: '#0069BD' }}
                >
                  <i className="ri-mail-send-line text-sm"></i>
                  Send Email to Admin
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
